import { Account } from "@services/account/types";
import { DeviceType } from "@common/components/ListDetailViews/DetailView/DeviceCard/const";
import { FlattenedDevice } from "@pages/Assets/DeviceProvisioning/const";
import { FlattenedLocation } from "@pages/Assets/LocationProvisioning/const";
import { FlattenedRoom } from "@pages/Assets/RoomProvisioning/const";
import {
  CallingMethod,
  CodecProtocol,
  Device,
  DeviceDetails,
  ManagementInterface,
  ManagementProtocol,
} from "@services/devices/types";
import { LocationEntity } from "@services/locations/types";
import { flattenObj } from "@common/validation/entityValidationUtils";

export type DeviceDetailsKeyMapper = {
  [k in keyof DeviceDetails]-?: `details.${k}`;
};
export type ReverseDeviceDetailsKeyMapper = {
  [k in keyof DeviceDetails as `details.${k}`]-?: k;
};

export type DeviceAggregatorKeyMapper = {
  [k in keyof Device]-?: `aggregator.${k}`;
};
export type ReverseDeviceAggregatorKeyMapper = {
  [k in keyof Device as `aggregator.${k}`]-?: k;
};

export type DeviceAggregatorLocationKeyMapper = {
  [k in keyof LocationEntity]-?: `aggregator.room.location.${k}`;
};

export type DeviceAggregatorAccountKeyMapper = {
  [k in keyof Account]-?: `aggregator.room.location.account.${k}`;
};

export type DeviceRoomLocationKeyMapper = {
  [k in keyof FlattenedLocation]-?: `room.location.${k}`;
};
export type ReverseDeviceRoomLocationKeyMapper = {
  [k in keyof FlattenedLocation as `room.location.${k}`]-?: k;
};

export type DeviceRoomKeyMapper = {
  [k in keyof FlattenedRoom]-?: `room.${k}`;
};
export type ReverseDeviceRoomsKeyMapper = {
  [k in keyof FlattenedRoom as `room.${k}`]-?: k;
};

/**
 * Types to properly access the management interfaces' properties
 */
export type ManagementInterfaceProtocolKey =
  | ManagementProtocol.HTTP
  | ManagementProtocol.SSH
  | ManagementProtocol.TELNET;
export type ManagementInterfacePropertyKey = keyof Pick<
  ManagementInterface,
  "localPort" | "remotePort" | "username" | "password" | "secure"
>;
export type FlattenedManagementInterfaceKey =
  `details.managementInterfaces.${ManagementInterfaceProtocolKey}.${ManagementInterfacePropertyKey}`;
export type FlattenedManagementInterfaceProperties = {
  [k in FlattenedManagementInterfaceKey]?: unknown;
};
export const splitManagementInterfaceProperty = (
  compositeKey: FlattenedManagementInterfaceKey
): [ManagementProtocol, ManagementInterfacePropertyKey] => {
  const [protocol, property] = compositeKey
    .replace("details.managementInterfaces.", "")
    .split(".") as [
    ManagementInterfaceProtocolKey,
    ManagementInterfacePropertyKey
  ];
  return [protocol, property];
};
export const isManagementInterfaceProperty = (
  x: KeyofDevice
): x is keyof FlattenedManagementInterfaceProperties =>
  x.startsWith("details.managementInterfaces.");
export type KeyofDeviceDetails = DeviceDetailsKeyMapper[keyof DeviceDetails];
export type KeyofDeviceAggregator = DeviceAggregatorKeyMapper[keyof Device];
export type KeyofAggregatorLocation =
  DeviceAggregatorLocationKeyMapper[keyof LocationEntity];
export type KeyofAggregatorAccount =
  DeviceAggregatorAccountKeyMapper[keyof Account];
export type KeyofDeviceRoom = DeviceRoomKeyMapper[keyof FlattenedRoom];
export type KeyofDeviceLocation =
  DeviceRoomLocationKeyMapper[keyof FlattenedLocation];
export type KeyofDevice =
  | keyof Device
  | KeyofDeviceLocation
  | KeyofDeviceRoom
  | KeyofDeviceDetails
  | KeyofDeviceAggregator
  | KeyofAggregatorLocation
  | KeyofAggregatorAccount
  | FlattenedManagementInterfaceKey;

export type KeysOfDevice = KeyofDevice[];

export type RequiredKeysOfDevice = Array<KeyofDevice | KeysOfDevice>;

export const isSimpleAsset = (device: Device) =>
  device.type == DeviceType.ASSET;
export const fieldIsEmpty = (field: any): boolean => {
  if (field === undefined || field === null) return true;
  switch (typeof field) {
    case "object":
    case "string":
      if (field.length === 0) return true;
    case "number":
    default:
      return false;
  }
};

export const deepDeviceGet = (device: Device, key: KeyofDevice) => {
  if (isManagementInterfaceProperty(key)) {
    const [protocol, property] = splitManagementInterfaceProperty(key);
    return device.details?.managementInterfaces?.find(
      (x) => x.protocol === protocol
    )?.[property];
  }
  return (flattenObj(device) as FlattenedDevice)[key];
};

export const allowedCallingMethod: { [k in CodecProtocol]: CallingMethod[] } = {
  [CodecProtocol.DualH323SIP]: [
    CallingMethod.Auto,
    CallingMethod.SIPURI,
    CallingMethod.SIPIP,
    CallingMethod.H323E164,
    CallingMethod.H323IP,
    CallingMethod.None,
  ],
  [CodecProtocol.H323Only]: [
    CallingMethod.Auto,
    CallingMethod.H323E164,
    CallingMethod.H323IP,
    CallingMethod.None,
  ],
  [CodecProtocol.SIPOnly]: [
    CallingMethod.Auto,
    CallingMethod.SIPURI,
    CallingMethod.SIPIP,
    CallingMethod.None,
  ],
  [CodecProtocol.ISDN]: [
    CallingMethod.Auto,
    CallingMethod.ISDNNumber,
    CallingMethod.None,
  ],
  [CodecProtocol.MSTeams]: [
    CallingMethod.Auto,
    CallingMethod.MSTeamsID,
    CallingMethod.None,
  ],
  [CodecProtocol.None]: [
    CallingMethod.Auto,
    CallingMethod.ISDNNumber,
    CallingMethod.MSTeamsID,
    CallingMethod.SIPURI,
    CallingMethod.SIPIP,
    CallingMethod.H323E164,
    CallingMethod.H323IP,
  ],
};
